/**
 * Function Service for Dynamic Functions in API Testing
 * Supports Postman-like dynamic variables and custom functions
 */

// Simple UUID v4 generator (to avoid external dependency)
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Function registry - maps function names to their implementations
type FunctionImplementation = (...args: any[]) => any;

export interface FunctionDefinition {
  name: string;
  category: string;
  description: string;
  syntax: string;
  example: string;
  implementation: FunctionImplementation;
}

// Generate random string helpers
const randomString = (length: number, chars: string): string => {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const randomAlphaNumeric = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return randomString(length, chars);
};

const randomAlpha = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  return randomString(length, chars);
};

const randomHex = (length: number): string => {
  const chars = '0123456789abcdef';
  return randomString(length, chars);
};

// Fake data generators
const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Emily', 'James', 'Emma', 'Robert', 'Olivia'];
const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
const cities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'Austin'];
const countries = ['USA', 'Canada', 'UK', 'Germany', 'France', 'Spain', 'Italy', 'Japan', 'Australia', 'Brazil'];
const domains = ['example.com', 'test.com', 'demo.com', 'mail.com', 'email.com'];
const companies = ['Tech Corp', 'Data Systems', 'Cloud Solutions', 'Digital Services', 'Innovation Labs', 'Software Inc'];
const jobTitles = ['Software Engineer', 'Product Manager', 'Data Analyst', 'Designer', 'Marketing Manager', 'Sales Representative'];

const randomChoice = <T,>(array: T[]): T => array[Math.floor(Math.random() * array.length)];

// Date/Time formatting
const formatDate = (date: Date, format: string): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  
  const replacements: Record<string, string> = {
    'YYYY': date.getFullYear().toString(),
    'MM': pad(date.getMonth() + 1),
    'DD': pad(date.getDate()),
    'HH': pad(date.getHours()),
    'mm': pad(date.getMinutes()),
    'ss': pad(date.getSeconds()),
  };

  let result = format;
  Object.entries(replacements).forEach(([key, value]) => {
    result = result.replace(key, value);
  });
  
  return result;
};

// Substring validation - strict index boundaries (bounds only)
const validateSubstring = (text: string, start: number, end: number): void => {
  const length = text.length;

  if (start < 0 || end < 0) {
    throw new Error('Start and end indices must be non-negative');
  }

  if (start > end) {
    throw new Error('Start index cannot be greater than end index');
  }

  if (start >= length) {
    throw new Error('Start index exceeds string length');
  }

  if (end > length) {
    throw new Error('End index exceeds string length');
  }
};

// Centralized executors for dynamic functions
const executeSubstring = (args: any[]): string => {
  const text = args[0] == null ? '' : String(args[0]);
  const start = Number(args[1]);
  const end = args[2] !== undefined && args[2] !== null && args[2] !== ''
    ? Number(args[2])
    : text.length;

  if (!Number.isFinite(start) || !Number.isInteger(start)) {
    throw new Error('Start must be an integer');
  }
  if (!Number.isFinite(end) || !Number.isInteger(end)) {
    throw new Error('End must be an integer');
  }

  validateSubstring(text, start, end);
  return text.substring(start, end);
};

const executeTrim = (args: any[]): string => {
  const input = args[0] == null ? '' : String(args[0]);
  return input.trim();
};

const executeReplace = (args: any[]): string => {
  const text = args[0] == null ? '' : String(args[0]);
  const search = args[1] == null ? '' : String(args[1]);
  const replace = args[2] == null ? '' : String(args[2]);

  if (search.length === 0 || !text.includes(search)) {
    throw new Error('Search text not found in the input string.');
  }

  return text.replace(search, replace);
};

/** Coerce a function argument to a finite number (Math helpers / index args). */
export function toFiniteNumber(value: unknown, label = 'value'): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  throw new Error(`${label} must be a valid number`);
}

const MATH_FUNCTION_NAMES = new Set([
  '$add',
  '$subtract',
  '$multiply',
  '$divide',
  '$round',
  '$floor',
  '$ceil',
  '$abs',
  '$min',
  '$max',
]);

const BARE_NUMBER_RE = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/;

/**
 * Parse $fn(...) argument list with JSON typing:
 * - "10" / '10' → string "10" (concat-safe, preserves leading zeros)
 * - 10 → number 10 (math-safe)
 */
export function parseFunctionArgs(argsString: string | undefined): unknown[] {
  if (!argsString?.trim()) {
    return [];
  }
  let normalized = argsString.trim();
  for (let i = 0; i < 4; i++) {
    const next = normalized.replace(/\\"/g, '"').replace(/\\'/g, "'");
    if (next === normalized) {
      break;
    }
    normalized = next;
  }
  try {
    const parsed = JSON.parse(`[${normalized}]`) as unknown[];
    if (Array.isArray(parsed)) {
      return parsed.map((v) => (v === null || v === undefined ? '' : v));
    }
  } catch {
    /* fall through */
  }

  const argMatches = normalized.match(/(?:[^,"']|"[^"\\]*(?:\\.[^"\\]*)*"|'[^'\\]*(?:\\.[^'\\]*)*')+/g) || [];
  return argMatches.map((raw) => {
    const token = raw.trim();
    if (
      (token.startsWith('"') && token.endsWith('"')) ||
      (token.startsWith("'") && token.endsWith("'"))
    ) {
      try {
        return JSON.parse(token.startsWith("'") ? `"${token.slice(1, -1).replace(/\\'/g, "'").replace(/"/g, '\\"')}"` : token);
      } catch {
        return token.slice(1, -1);
      }
    }
    if (token === 'true') return true;
    if (token === 'false') return false;
    if (token === 'null') return null;
    if (BARE_NUMBER_RE.test(token)) {
      return Number(token);
    }
    return token;
  });
}

/**
 * Prepare args for a function call.
 * Math builtins coerce numeric strings → numbers.
 * Everything else keeps parsed types (quoted strings stay strings).
 */
export function processFunctionArgs(functionName: string, args: unknown[]): unknown[] {
  const name = functionName.startsWith('$') ? functionName : `$${functionName}`;
  if (MATH_FUNCTION_NAMES.has(name)) {
    return args.map((arg, i) => toFiniteNumber(arg, `Argument ${i + 1}`));
  }
  return args;
}

// Hash functions (simplified - would use crypto in production)
const simpleHash = async (text: string, algorithm: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest(algorithm, data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Function Definitions
export const functionDefinitions: FunctionDefinition[] = [
  // Dynamic Variables / Generators
  {
    name: '$guid',
    category: 'Dynamic Variables',
    description: 'Generate a GUID/UUID v4',
    syntax: '$guid',
    example: '$guid → "550e8400-e29b-41d4-a716-446655440000"',
    implementation: () => generateUUID(),
  },
  {
    name: '$timestamp',
    category: 'Dynamic Variables',
    description: 'Current Unix timestamp in milliseconds',
    syntax: '$timestamp',
    example: '$timestamp → "1705324800000"',
    implementation: () => Date.now().toString(),
  },
  {
    name: '$isoTimestamp',
    category: 'Dynamic Variables',
    description: 'ISO 8601 timestamp',
    syntax: '$isoTimestamp',
    example: '$isoTimestamp → "2026-01-15T12:30:45.123Z"',
    implementation: () => new Date().toISOString(),
  },
  {
    name: '$randomInt',
    category: 'Dynamic Variables',
    description: 'Random integer between 0 and 1000',
    syntax: '$randomInt',
    example: '$randomInt → "742" (or $randomInt(1, 100) for a range)',
    implementation: (min?: number, max?: number) => {
      if (min !== undefined && max !== undefined && !Number.isNaN(min) && !Number.isNaN(max)) {
        const lo = Math.min(min, max);
        const hi = Math.max(min, max);
        return Math.floor(Math.random() * (hi - lo + 1) + lo).toString();
      }
      return Math.floor(Math.random() * 1001).toString();
    },
  },
  {
    name: '$randomIntRange',
    category: 'Dynamic Variables',
    description: 'Random integer in specified range',
    syntax: '$randomIntRange(min, max)',
    example: '$randomIntRange(10, 100) → "57"',
    implementation: (min: number, max: number) => {
      return Math.floor(Math.random() * (max - min + 1) + min).toString();
    },
  },
  {
    name: '$randomFloat',
    category: 'Dynamic Variables',
    description: 'Random float between 0.0 and 1.0',
    syntax: '$randomFloat',
    example: '$randomFloat → "0.7234"',
    implementation: () => Math.random().toFixed(4),
  },
  {
    name: '$randomAlphaNumeric',
    category: 'Dynamic Variables',
    description: 'Random alphanumeric string',
    syntax: '$randomAlphaNumeric(length)',
    example: '$randomAlphaNumeric(10) → "aB3xK9mP2q"',
    implementation: (length: number = 10) => randomAlphaNumeric(length),
  },
  {
    name: '$randomString',
    category: 'Dynamic Variables',
    description: 'Random alphabetic string',
    syntax: '$randomString(length)',
    example: '$randomString(8) → "AbCdEfGh"',
    implementation: (length: number = 8) => randomAlpha(length),
  },
  {
    name: '$randomHex',
    category: 'Dynamic Variables',
    description: 'Random hexadecimal string',
    syntax: '$randomHex(length)',
    example: '$randomHex(16) → "a3f9b2c1d4e5f6a7"',
    implementation: (length: number = 16) => randomHex(length),
  },
  {
    name: '$randomBoolean',
    category: 'Dynamic Variables',
    description: 'Random boolean value',
    syntax: '$randomBoolean',
    example: '$randomBoolean → true or false (JSON boolean when used as sole field value)',
    implementation: () => Math.random() > 0.5,
  },

  // Date/Time Functions
  {
    name: '$now',
    category: 'Date/Time',
    description: 'Current date and time',
    syntax: '$now',
    example: '$now → "2026-01-15T12:30:45.123Z"',
    implementation: () => new Date().toISOString(),
  },
  {
    name: '$date',
    category: 'Date/Time',
    description: 'Current date in specified format',
    syntax: '$date(format)',
    example: '$date(YYYY-MM-DD) → "2026-01-15"',
    implementation: (format: string = 'YYYY-MM-DD') => formatDate(new Date(), format),
  },
  {
    name: '$dateAdd',
    category: 'Date/Time',
    description: 'Add days to current date',
    syntax: '$dateAdd(days)',
    example: '$dateAdd(7) → "2026-01-22"',
    implementation: (days: number) => {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },
  },
  {
    name: '$dateSubtract',
    category: 'Date/Time',
    description: 'Subtract days from current date',
    syntax: '$dateSubtract(days)',
    example: '$dateSubtract(7) → "2026-01-08"',
    implementation: (days: number) => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString().split('T')[0];
    },
  },
  {
    name: '$unixTimestamp',
    category: 'Date/Time',
    description: 'Unix timestamp in seconds',
    syntax: '$unixTimestamp',
    example: '$unixTimestamp → "1705324800"',
    implementation: () => Math.floor(Date.now() / 1000).toString(),
  },

  // String Manipulation
  {
    name: '$toUpperCase',
    category: 'String',
    description: 'Convert text to uppercase',
    syntax: '$toUpperCase(text)',
    example: '$toUpperCase(hello) → "HELLO"',
    implementation: (text: any) => String(text ?? '').toUpperCase(),
  },
  {
    name: '$toLowerCase',
    category: 'String',
    description: 'Convert text to lowercase',
    syntax: '$toLowerCase(text)',
    example: '$toLowerCase(HELLO) → "hello"',
    implementation: (text: any) => String(text ?? '').toLowerCase(),
  },
  {
    name: '$trim',
    category: 'String',
    description: 'Remove leading/trailing whitespace',
    syntax: '$trim(text)',
    example: '$trim(  hello  ) → "hello"',
    implementation: (text: any) => executeTrim([text]),
  },
  {
    name: '$substring',
    category: 'String',
    description: 'Extract substring',
    syntax: '$substring(text, start, end)',
    example: '$substring(hello, 0, 3) → "hel"',
    implementation: (text: any, start: any, end?: any) =>
      executeSubstring([text, start, end]),
  },
  {
    name: '$replace',
    category: 'String',
    description: 'Replace text',
    syntax: '$replace(text, search, replace)',
    example: '$replace(hello world, world, there) → "hello there"',
    implementation: (text: any, search: any, replace: any) =>
      executeReplace([text, search, replace]),
  },
  {
    name: '$concat',
    category: 'String',
    description: 'Concatenate strings',
    syntax: '$concat(text1, text2, ...)',
    example: '$concat(hello, world) → "helloworld"',
    implementation: (...args: any[]) => args.map((a) => (a == null ? '' : String(a))).join(''),
  },
  {
    name: '$length',
    category: 'String',
    description: 'Get string length',
    syntax: '$length(text)',
    example: '$length(hello) → "5"',
    implementation: (text: any) => String(text ?? '').length,
  },
  {
    name: '$reverse',
    category: 'String',
    description: 'Reverse string',
    syntax: '$reverse(text)',
    example: '$reverse(hello) → "olleh"',
    implementation: (text: any) => String(text ?? '').split('').reverse().join(''),
  },
  {
    name: '$repeat',
    category: 'String',
    description: 'Repeat text',
    syntax: '$repeat(text, count)',
    example: '$repeat(a, 3) → "aaa"',
    implementation: (text: any, count: any) => {
      const n = Number(count);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        throw new Error('Repeat count must be a non-negative integer');
      }
      return String(text ?? '').repeat(n);
    },
  },

  // Encoding/Decoding
  {
    name: '$base64Encode',
    category: 'Encoding',
    description: 'Base64 encode',
    syntax: '$base64Encode(text)',
    example: '$base64Encode(hello) → "aGVsbG8="',
    implementation: (text: string) => btoa(text),
  },
  {
    name: '$base64Decode',
    category: 'Encoding',
    description: 'Base64 decode',
    syntax: '$base64Decode(text)',
    example: '$base64Decode(aGVsbG8=) → "hello"',
    implementation: (text: string) => atob(text),
  },
  {
    name: '$urlEncode',
    category: 'Encoding',
    description: 'URL encode',
    syntax: '$urlEncode(text)',
    example: '$urlEncode(hello world) → "hello%20world"',
    implementation: (text: string) => encodeURIComponent(text),
  },
  {
    name: '$urlDecode',
    category: 'Encoding',
    description: 'URL decode',
    syntax: '$urlDecode(text)',
    example: '$urlDecode(hello%20world) → "hello world"',
    implementation: (text: string) => decodeURIComponent(text),
  },
  {
    name: '$hexEncode',
    category: 'Encoding',
    description: 'Hex encode',
    syntax: '$hexEncode(text)',
    example: '$hexEncode(hello) → "68656c6c6f"',
    implementation: (text: string) => {
      return Array.from(text).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    },
  },
  {
    name: '$hexDecode',
    category: 'Encoding',
    description: 'Hex decode',
    syntax: '$hexDecode(text)',
    example: '$hexDecode(68656c6c6f) → "hello"',
    implementation: (text: string) => {
      const hex = text.match(/.{1,2}/g) || [];
      return hex.map(byte => String.fromCharCode(parseInt(byte, 16))).join('');
    },
  },

  // Hashing (async functions need special handling)
  {
    name: '$md5',
    category: 'Hashing',
    description: 'MD5 hash (Note: Not cryptographically secure)',
    syntax: '$md5(text)',
    example: '$md5(hello) → "5d41402abc4b2a76b9719d911017c592"',
    implementation: async (text: string) => {
      // Simple MD5 implementation - in production use crypto library
      return await simpleHash(text, 'SHA-1'); // Fallback to SHA-1
    },
  },
  {
    name: '$sha256',
    category: 'Hashing',
    description: 'SHA-256 hash',
    syntax: '$sha256(text)',
    example: '$sha256(hello) → "2cf24dba5fb0a30e26e83b2ac5b9e29e1b161e5c1fa7425e73043362938b9824"',
    implementation: async (text: string) => await simpleHash(text, 'SHA-256'),
  },
  {
    name: '$sha512',
    category: 'Hashing',
    description: 'SHA-512 hash',
    syntax: '$sha512(text)',
    example: '$sha512(hello) → "9b71d224bd62f3785d96d46ad3ea3d73..."',
    implementation: async (text: string) => await simpleHash(text, 'SHA-512'),
  },

  // Math Functions
  {
    name: '$add',
    category: 'Math',
    description: 'Addition',
    syntax: '$add(a, b)',
    example: '$add(10, 5) → "15"',
    implementation: (a: any, b: any) => toFiniteNumber(a, 'a') + toFiniteNumber(b, 'b'),
  },
  {
    name: '$subtract',
    category: 'Math',
    description: 'Subtraction',
    syntax: '$subtract(a, b)',
    example: '$subtract(10, 5) → "5"',
    implementation: (a: any, b: any) => toFiniteNumber(a, 'a') - toFiniteNumber(b, 'b'),
  },
  {
    name: '$multiply',
    category: 'Math',
    description: 'Multiplication',
    syntax: '$multiply(a, b)',
    example: '$multiply(10, 5) → "50"',
    implementation: (a: any, b: any) => toFiniteNumber(a, 'a') * toFiniteNumber(b, 'b'),
  },
  {
    name: '$divide',
    category: 'Math',
    description: 'Division',
    syntax: '$divide(a, b)',
    example: '$divide(10, 5) → "2"',
    implementation: (a: any, b: any) => {
      const divisor = toFiniteNumber(b, 'b');
      if (divisor === 0) {
        throw new Error('Division by zero');
      }
      return toFiniteNumber(a, 'a') / divisor;
    },
  },
  {
    name: '$round',
    category: 'Math',
    description: 'Round number',
    syntax: '$round(number)',
    example: '$round(3.7) → "4"',
    implementation: (num: any) => Math.round(toFiniteNumber(num, 'number')),
  },
  {
    name: '$floor',
    category: 'Math',
    description: 'Floor number',
    syntax: '$floor(number)',
    example: '$floor(3.7) → "3"',
    implementation: (num: any) => Math.floor(toFiniteNumber(num, 'number')),
  },
  {
    name: '$ceil',
    category: 'Math',
    description: 'Ceiling number',
    syntax: '$ceil(number)',
    example: '$ceil(3.2) → "4"',
    implementation: (num: any) => Math.ceil(toFiniteNumber(num, 'number')),
  },
  {
    name: '$abs',
    category: 'Math',
    description: 'Absolute value',
    syntax: '$abs(number)',
    example: '$abs(-5) → "5"',
    implementation: (num: any) => Math.abs(toFiniteNumber(num, 'number')),
  },
  {
    name: '$min',
    category: 'Math',
    description: 'Minimum value',
    syntax: '$min(a, b)',
    example: '$min(10, 5) → "5"',
    implementation: (a: any, b: any) => Math.min(toFiniteNumber(a, 'a'), toFiniteNumber(b, 'b')),
  },
  {
    name: '$max',
    category: 'Math',
    description: 'Maximum value',
    syntax: '$max(a, b)',
    example: '$max(10, 5) → "10"',
    implementation: (a: any, b: any) => Math.max(toFiniteNumber(a, 'a'), toFiniteNumber(b, 'b')),
  },

  // Fake Data Generators
  {
    name: '$randomFirstName',
    category: 'Fake Data',
    description: 'Random first name',
    syntax: '$randomFirstName',
    example: '$randomFirstName → "John"',
    implementation: () => randomChoice(firstNames),
  },
  {
    name: '$randomLastName',
    category: 'Fake Data',
    description: 'Random last name',
    syntax: '$randomLastName',
    example: '$randomLastName → "Smith"',
    implementation: () => randomChoice(lastNames),
  },
  {
    name: '$randomFullName',
    category: 'Fake Data',
    description: 'Random full name',
    syntax: '$randomFullName',
    example: '$randomFullName → "John Smith"',
    implementation: () => `${randomChoice(firstNames)} ${randomChoice(lastNames)}`,
  },
  {
    name: '$randomEmail',
    category: 'Fake Data',
    description: 'Random email address',
    syntax: '$randomEmail',
    example: '$randomEmail → "john.smith@example.com"',
    implementation: () => {
      const firstName = randomChoice(firstNames).toLowerCase();
      const lastName = randomChoice(lastNames).toLowerCase();
      const domain = randomChoice(domains);
      return `${firstName}.${lastName}@${domain}`;
    },
  },
  {
    name: '$randomPhoneNumber',
    category: 'Fake Data',
    description: 'Random phone number',
    syntax: '$randomPhoneNumber',
    example: '$randomPhoneNumber → "+1-555-123-4567"',
    implementation: () => {
      const area = Math.floor(Math.random() * 900 + 100);
      const prefix = Math.floor(Math.random() * 900 + 100);
      const line = Math.floor(Math.random() * 9000 + 1000);
      return `+1-${area}-${prefix}-${line}`;
    },
  },
  {
    name: '$randomCity',
    category: 'Fake Data',
    description: 'Random city name',
    syntax: '$randomCity',
    example: '$randomCity → "New York"',
    implementation: () => randomChoice(cities),
  },
  {
    name: '$randomCountry',
    category: 'Fake Data',
    description: 'Random country name',
    syntax: '$randomCountry',
    example: '$randomCountry → "USA"',
    implementation: () => randomChoice(countries),
  },
  {
    name: '$randomZipCode',
    category: 'Fake Data',
    description: 'Random ZIP code',
    syntax: '$randomZipCode',
    example: '$randomZipCode → "12345"',
    implementation: () => Math.floor(Math.random() * 90000 + 10000).toString(),
  },
  {
    name: '$randomCompanyName',
    category: 'Fake Data',
    description: 'Random company name',
    syntax: '$randomCompanyName',
    example: '$randomCompanyName → "Tech Corp"',
    implementation: () => randomChoice(companies),
  },
  {
    name: '$randomJobTitle',
    category: 'Fake Data',
    description: 'Random job title',
    syntax: '$randomJobTitle',
    example: '$randomJobTitle → "Software Engineer"',
    implementation: () => randomChoice(jobTitles),
  },
  {
    name: '$randomIP',
    category: 'Fake Data',
    description: 'Random IP address',
    syntax: '$randomIP',
    example: '$randomIP → "192.168.1.100"',
    implementation: () => {
      return Array(4).fill(0).map(() => Math.floor(Math.random() * 256)).join('.');
    },
  },
  {
    name: '$randomUserAgent',
    category: 'Fake Data',
    description: 'Random user agent string',
    syntax: '$randomUserAgent',
    example: '$randomUserAgent → "Mozilla/5.0..."',
    implementation: () => {
      const agents = [
        'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
        'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
        'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:89.0) Gecko/20100101 Firefox/89.0',
      ];
      return randomChoice(agents);
    },
  },
  {
    name: '$randomChoice',
    category: 'Utility',
    description: 'Random choice from list',
    syntax: '$randomChoice(option1, option2, ...)',
    example: '$randomChoice(red, green, blue) → "green"',
    implementation: (...options: string[]) => randomChoice(options),
  },
];

// Create a map for quick lookup
const functionMap = new Map<string, FunctionDefinition>();
functionDefinitions.forEach(func => {
  functionMap.set(func.name, func);
});

/** Common aliases (e.g. $lowercase → $toLowerCase). */
const FUNCTION_NAME_ALIASES: Record<string, string> = {
  $lowercase: '$toLowerCase',
  $uppercase: '$toUpperCase',
};

function resolveFunctionName(functionName: string): string {
  const withDollar = functionName.startsWith('$') ? functionName : `$${functionName}`;
  return FUNCTION_NAME_ALIASES[withDollar] ?? withDollar;
}

/**
 * Request function-reference syntax: $[functionName] or $[functionName](args).
 * Name rules match Create Function validation (letter first, then letters/digits/_).
 * Plain $name / $100 / hello$guid are NOT references. GraphQL [$userId] is NOT a reference.
 * JSONPath $[0].id is NOT a reference (numeric index ≠ function name).
 */
const FUNCTION_REF_NAME = '[A-Za-z][A-Za-z0-9_]*';
const FUNCTION_REF_NAME_RE = new RegExp(`^${FUNCTION_REF_NAME}$`);
const SOLE_FUNCTION_CALL_RE = new RegExp(`^\\$\\[(${FUNCTION_REF_NAME})\\](?:\\(([\\s\\S]*)\\))?$`);
/** Inline tokens inside URLs/bodies (global). */
const INLINE_FUNCTION_CALL_RE = new RegExp(`\\$\\[(${FUNCTION_REF_NAME})\\](?:\\(([^)]*)\\))?`, 'g');
/** Detect remaining unresolved request function tokens. */
export const UNRESOLVED_FUNCTION_REF_RE = new RegExp(`\\$\\[(${FUNCTION_REF_NAME})\\]`);

/** JSONPath / non-function `$[` starts: $[0], $[*], $[?(@...)], $['key'], $["key"]. */
function isNonFunctionDollarBracket(innerFirstChar: string): boolean {
  return (
    (innerFirstChar >= '0' && innerFirstChar <= '9') ||
    innerFirstChar === '*' ||
    innerFirstChar === '?' ||
    innerFirstChar === "'" ||
    innerFirstChar === '"'
  );
}

function invalidFunctionReferenceToken(text: string, start: number, innerStart: number): string {
  const close = text.indexOf(']', innerStart);
  if (close >= 0) {
    return text.slice(start, close + 1);
  }
  let end = text.length;
  const nl = text.indexOf('\n', innerStart);
  const quote = text.indexOf('"', innerStart);
  if (nl >= 0 && nl < end) end = nl;
  if (quote >= 0 && quote < end) end = quote;
  return text.slice(start, end);
}

/**
 * Stage 1 — syntax: every attempted `$[` that is not JSONPath must be `$[validName]`.
 * Existence (stage 2) is checked later by evaluateFunctionRaw.
 */
export function assertFunctionReferenceSyntax(text: string): void {
  if (!text || !text.includes('$[')) {
    return;
  }
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf('$[', i);
    if (start < 0) {
      return;
    }
    const innerStart = start + 2;
    if (innerStart >= text.length) {
      throw new Error('Invalid function reference: $[');
    }
    if (isNonFunctionDollarBracket(text[innerStart])) {
      i = innerStart;
      continue;
    }
    const close = text.indexOf(']', innerStart);
    const token = invalidFunctionReferenceToken(text, start, innerStart);
    if (close < 0) {
      throw new Error(`Invalid function reference: ${token}`);
    }
    const inner = text.slice(innerStart, close);
    if (!FUNCTION_REF_NAME_RE.test(inner)) {
      throw new Error(`Invalid function reference: ${token}`);
    }
    i = close + 1;
  }
}

/** True when text contains a complete $[functionName] request reference. */
export function isFunctionReference(text: string): boolean {
  if (!text || !text.includes('$[')) return false;
  UNRESOLVED_FUNCTION_REF_RE.lastIndex = 0;
  return UNRESOLVED_FUNCTION_REF_RE.test(text);
}

/**
 * Convert internal/display identity ($guid) or legacy syntax to request reference ($[guid]).
 * Idempotent for already-wrapped $[name] forms.
 */
export function toRequestFunctionReference(text: string): string {
  if (!text) return text;
  return text.replace(
    /\$\[([A-Za-z][A-Za-z0-9_]*)\]|\$([A-Za-z][A-Za-z0-9_]*)/g,
    (_m, bracketed: string | undefined, bare: string | undefined) => `$[${bracketed || bare}]`,
  );
}

/** True when a JSON string field value is only a single $[function](...) call. */
export function parseSoleFunctionCall(value: string): { name: string; argsString?: string } | null {
  const trimmed = value.trim();
  const match = trimmed.match(SOLE_FUNCTION_CALL_RE);
  if (!match) {
    return null;
  }
  // Map request ref $[guid] → existing registry key $guid
  return { name: '$' + match[1], argsString: match[2] };
}

/** Server-defined custom functions (JS or STATIC), keyed by lower-case name without $. */
export type ServerCustomFunction = {
  id: number;
  name: string;
  type?: string;
  code?: string | null;
  value?: string | null;
  version?: number;
  isActive?: boolean;
};

export const MAX_CUSTOM_FUNCTIONS_PER_USER = 100;

/** Strip leading $ for lookup/errors; do not change case or other characters. */
function bareFunctionName(functionName: string): string {
  return functionName.startsWith('$') ? functionName.slice(1) : functionName;
}

function throwUnknownFunction(functionName: string): never {
  throw new Error(`Unknown function: ${bareFunctionName(functionName)}`);
}

export type CustomFunctionsListResponse = {
  functions: ServerCustomFunction[];
  activeCount: number;
  maxAllowed: number;
};

let customFunctionsByName = new Map<string, ServerCustomFunction>();
let customFunctionsLoadPromise: Promise<void> | null = null;
let customFunctionsCacheScopeKey: string | null = null;

function getCustomFunctionsCacheScopeKey(): string {
  if (typeof window === 'undefined') {
    return 'server';
  }
  const hostSlug = window.location.hostname.includes('.localhost')
    ? window.location.hostname.split('.')[0]
    : '';
  let userId = '';
  let orgId = '';
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    userId = user?.id != null ? String(user.id) : '';
    orgId = user?.organizationId != null ? String(user.organizationId) : '';
  } catch {
    /* ignore */
  }
  return `${hostSlug}:${orgId}:${userId}`;
}

function parseCustomFunctionsResponse(data: unknown): CustomFunctionsListResponse {
  if (Array.isArray(data)) {
    const list = data as ServerCustomFunction[];
    return { functions: list, activeCount: list.length, maxAllowed: MAX_CUSTOM_FUNCTIONS_PER_USER };
  }
  if (data && typeof data === 'object' && Array.isArray((data as CustomFunctionsListResponse).functions)) {
    const payload = data as CustomFunctionsListResponse;
    return {
      functions: payload.functions,
      activeCount: payload.activeCount ?? payload.functions.length,
      maxAllowed: payload.maxAllowed ?? MAX_CUSTOM_FUNCTIONS_PER_USER,
    };
  }
  return { functions: [], activeCount: 0, maxAllowed: MAX_CUSTOM_FUNCTIONS_PER_USER };
}

export const CUSTOM_FUNCTIONS_CHANGED_EVENT = 'custom-functions-changed';

export function invalidateCustomFunctionsCache(): void {
  customFunctionsLoadPromise = null;
  customFunctionsByName = new Map();
  customFunctionsCacheScopeKey = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CUSTOM_FUNCTIONS_CHANGED_EVENT));
  }
}

export function primeCustomFunctionsCache(list: ServerCustomFunction[]): void {
  const next = new Map<string, ServerCustomFunction>();
  for (const f of list) {
    if (f?.name) {
      next.set(f.name, f);
    }
  }
  customFunctionsByName = next;
  customFunctionsCacheScopeKey = getCustomFunctionsCacheScopeKey();
  customFunctionsLoadPromise = Promise.resolve();
}

export async function ensureCustomFunctionsLoaded(): Promise<void> {
  const scopeKey = getCustomFunctionsCacheScopeKey();
  if (customFunctionsCacheScopeKey && customFunctionsCacheScopeKey !== scopeKey) {
    invalidateCustomFunctionsCache();
  }
  if (customFunctionsLoadPromise) {
    return customFunctionsLoadPromise;
  }
  customFunctionsLoadPromise = (async () => {
    try {
      const { apiService } = await import('./apiService');
      const res = await apiService.get('/functions');
      const parsed = parseCustomFunctionsResponse(res?.data);
      primeCustomFunctionsCache(parsed.functions);
    } catch {
      /* ignore */
    }
  })();
  return customFunctionsLoadPromise;
}

function parseStoredStaticValue(raw: string | null | undefined): unknown {
  if (raw == null || raw === '') {
    return '';
  }
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * If a custom/JSON function returns a string that is itself JSON (often pretty-printed
 * with newlines), unwrap once so sole-field embedding does not double-encode `\n`.
 * Exported for unit tests (CE-1096).
 */
export function unwrapJsonSizedString(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (
    !(
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    )
  ) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function formatSubstitutionResult(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  const unwrapped = unwrapJsonSizedString(value);
  if (typeof unwrapped === 'object') {
    return JSON.stringify(unwrapped);
  }
  return String(unwrapped);
}

async function evaluateCustomFunctionRawFromServer(functionName: string, args: unknown[]): Promise<unknown> {
  await ensureCustomFunctionsLoaded();
  const custom = customFunctionsByName.get(bareFunctionName(functionName));
  if (!custom) {
    throwUnknownFunction(functionName);
  }
  const processedArgs = processFunctionArgs(functionName, args);
  const t = (custom.type || 'JS').toUpperCase();
  if (t === 'STATIC') {
    return parseStoredStaticValue(custom.value ?? undefined);
  }
  const { apiService } = await import('./apiService');
  const res = await apiService.post('/functions/test', {
    type: 'JS',
    code: custom.code,
    value: null,
    args: processedArgs,
  });
  return res?.data?.result;
}

function evaluateCustomStaticRawSync(functionName: string): unknown {
  const custom = customFunctionsByName.get(bareFunctionName(functionName));
  if (!custom) {
    throwUnknownFunction(functionName);
  }
  const t = (custom.type || 'JS').toUpperCase();
  if (t !== 'STATIC') {
    throwUnknownFunction(functionName);
  }
  return parseStoredStaticValue(custom.value ?? undefined);
}

/**
 * Evaluate a function and return the raw JS value (boolean, number, string, etc.).
 */
export async function evaluateFunctionRaw(functionName: string, ...args: unknown[]): Promise<unknown> {
  const resolvedName = resolveFunctionName(functionName);
  const func = functionMap.get(resolvedName);

  if (func) {
    const processedArgs = processFunctionArgs(resolvedName, args);
    const result = await func.implementation(...processedArgs);
    return result;
  }

  return evaluateCustomFunctionRawFromServer(resolvedName, args);
}

export function evaluateFunctionSyncRaw(functionName: string, ...args: unknown[]): unknown {
  const resolvedName = resolveFunctionName(functionName);
  const func = functionMap.get(resolvedName);

  if (func) {
    const processedArgs = processFunctionArgs(resolvedName, args);
    const result = func.implementation(...processedArgs);
    if (result instanceof Promise) {
      throw new Error(`Function ${resolvedName} is async and cannot be evaluated synchronously`);
    }
    return result;
  }

  return evaluateCustomStaticRawSync(resolvedName);
}

async function resolveFunctionsInJsonValue(value: unknown): Promise<unknown> {
  if (typeof value === 'string') {
    const sole = parseSoleFunctionCall(value);
    if (sole) {
      const args = parseFunctionArgs(sole.argsString);
      const raw = await evaluateFunctionRaw(sole.name, ...args);
      // CE-1096: JSON/STATIC helpers that return pretty-printed JSON strings
      return unwrapJsonSizedString(raw);
    }
    if (isFunctionReference(value)) {
      return evaluateFunctionsInPlainText(value);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => resolveFunctionsInJsonValue(item)));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      out[key] = await resolveFunctionsInJsonValue(nested);
    }
    return out;
  }
  return value;
}

/** Replace $[fn](...) inside plain text (URLs, partial strings, non-JSON bodies). */
async function evaluateFunctionsInPlainText(text: string): Promise<string> {
  let result = text;
  const matches = Array.from(text.matchAll(INLINE_FUNCTION_CALL_RE));

  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const fullMatch = match[0];
    // Map $[guid] → existing registry key $guid
    const functionName = '$' + match[1];
    const argsString = match[2];

    const args = parseFunctionArgs(argsString);
    const raw = await evaluateFunctionRaw(functionName, ...args);
    const replacement = formatSubstitutionResult(raw);

    const startIndex = match.index!;
    const endIndex = startIndex + fullMatch.length;
    result = result.substring(0, startIndex) + replacement + result.substring(endIndex);
  }

  return result;
}

/**
 * Evaluate a function call
 * @param functionName - The name of the function (e.g., "$guid")
 * @param args - Arguments to pass to the function
 * @returns The result of the function call
 */
export const evaluateFunction = async (functionName: string, ...args: any[]): Promise<string> => {
  try {
    const raw = await evaluateFunctionRaw(functionName, ...args);
    return formatSubstitutionResult(raw);
  } catch (error: any) {
    console.error(`Error evaluating function ${functionName}:`, error);
    throw new Error(error?.message || `Function ${functionName} failed`);
  }
};

/**
 * Evaluate a function call synchronously (for non-async functions)
 * @param functionName - The name of the function (e.g., "$guid")
 * @param args - Arguments to pass to the function
 * @returns The result of the function call
 */
export const evaluateFunctionSync = (functionName: string, ...args: any[]): string => {
  try {
    const raw = evaluateFunctionSyncRaw(functionName, ...args);
    return formatSubstitutionResult(raw);
  } catch (error: any) {
    console.error(`Error evaluating function ${functionName}:`, error);
    throw new Error(`Function ${functionName} failed: ${error.message}`);
  }
};

/**
 * Resolve $[functionName] / $[functionName](args) calls in text (custom JS via backend + built-ins).
 * Plain $name / $100 / hello$guid stay literal. GraphQL [$var] is untouched.
 * JSON bodies: when a field value is ONLY $[fn](...), preserve boolean/number types.
 * Parse errors fall through to plain-text replacement; evaluation errors still fall back
 * to plain-text so a single bad field cannot leave the whole payload unresolved.
 */
export async function resolveFunctionsInText(text: string): Promise<string> {
  if (!text || !text.includes('$[')) {
    return text;
  }
  assertFunctionReferenceSyntax(text);
  if (!isFunctionReference(text)) {
    return text;
  }
  await ensureCustomFunctionsLoaded();

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return evaluateFunctionsInPlainText(text);
  }

  try {
    const resolved = await resolveFunctionsInJsonValue(parsed);
    return JSON.stringify(resolved);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err ?? '');
    if (/Invalid function reference/i.test(msg)) {
      throw err;
    }
    console.error('JSON function resolution failed; falling back to plain-text:', err);
    return evaluateFunctionsInPlainText(text);
  }
}

/**
 * Last-mile resolution for any outbound string body/URL/header that may still contain $[fn](...).
 * Safe to call repeatedly; no-ops when there is no $[...] reference.
 */
export async function ensureFunctionsResolvedInText(text: string | null | undefined): Promise<string> {
  if (text == null || text === '') {
    return text == null ? '' : String(text);
  }
  const value = String(text);
  if (!value.includes('$[')) {
    return value;
  }
  return resolveFunctionsInText(value);
}

export const evaluateFunctionsInText = resolveFunctionsInText;

/**
 * Synchronous $[function] resolution for assertion / rule-builder comparisons.
 * Built-ins and STATIC customs resolve immediately; async/JS tokens stay as-is.
 * JSONPath `$[0].id` is not a function reference and is left unchanged.
 */
export function resolveFunctionsSyncInText(text: string): string {
  if (!text || !text.includes('$[')) {
    return text;
  }
  assertFunctionReferenceSyntax(text);
  if (!isFunctionReference(text)) {
    return text;
  }

  const matches = Array.from(text.matchAll(INLINE_FUNCTION_CALL_RE));
  let result = text;
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const fullMatch = match[0];
    const functionName = '$' + match[1];
    const argsString = match[2];
    try {
      const args = parseFunctionArgs(argsString);
      const raw = evaluateFunctionSyncRaw(functionName, ...args);
      const replacement = formatSubstitutionResult(raw);
      const startIndex = match.index!;
      const endIndex = startIndex + fullMatch.length;
      result = result.substring(0, startIndex) + replacement + result.substring(endIndex);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error ?? '');
      if (/async|unknown function|cannot be evaluated synchronously/i.test(msg)) {
        continue;
      }
      throw error instanceof Error ? error : new Error(msg);
    }
  }
  return result;
}

/**
 * Assertion expected-value resolution: load custom functions, then evaluate built-ins,
 * STATIC, and JS customs. Unknown / failed tokens stay as-is so validation can continue.
 * JSONPath `$[0].id` is left unchanged.
 */
export async function resolveFunctionsInTextLenient(text: string): Promise<string> {
  if (!text || !text.includes('$[')) {
    return text;
  }
  assertFunctionReferenceSyntax(text);
  if (!isFunctionReference(text)) {
    return text;
  }
  await ensureCustomFunctionsLoaded();

  const matches = Array.from(text.matchAll(INLINE_FUNCTION_CALL_RE));
  let result = text;
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const fullMatch = match[0];
    const functionName = '$' + match[1];
    const argsString = match[2];
    try {
      const args = parseFunctionArgs(argsString);
      const raw = await evaluateFunctionRaw(functionName, ...args);
      const replacement = formatSubstitutionResult(raw);
      const startIndex = match.index!;
      const endIndex = startIndex + fullMatch.length;
      result = result.substring(0, startIndex) + replacement + result.substring(endIndex);
    } catch {
      continue;
    }
  }
  return result;
}

/**
 * Get all available functions grouped by category
 */
export const getFunctionsByCategory = (): Record<string, FunctionDefinition[]> => {
  const grouped: Record<string, FunctionDefinition[]> = {};
  
  functionDefinitions.forEach(func => {
    if (!grouped[func.category]) {
      grouped[func.category] = [];
    }
    grouped[func.category].push(func);
  });
  
  return grouped;
};

/**
 * Search functions by name or description
 */
export const searchFunctions = (query: string): FunctionDefinition[] => {
  const lowerQuery = query.toLowerCase();
  return functionDefinitions.filter(func =>
    func.name.toLowerCase().includes(lowerQuery) ||
    func.description.toLowerCase().includes(lowerQuery) ||
    func.category.toLowerCase().includes(lowerQuery)
  );
};

const functionService = {
  evaluateFunction,
  evaluateFunctionsInText,
  getFunctionsByCategory,
  searchFunctions,
  functionDefinitions,
};

export default functionService;
